/**
 * Role capabilities, and the one that matters most: who may see a phone number.
 *
 * A phone number is PII. The mock data carries them because the screens have to be judged with
 * real-looking rows, but no component reads `contact.phone` directly — they all go through
 * `phoneFor()`. When the real API lands it will redact server-side, and this stays as the
 * second lock rather than becoming a lie.
 *
 * Rank, highest first: owner > manager > rep > ops (the order the plan lists them in).
 *
 * Author: Danish Khan
 */
import type { Role } from './types.ts';

const RANK: Record<Role, number> = { owner: 4, manager: 3, rep: 2, ops: 1 };

export function atLeast(role: Role, min: Role): boolean {
  return RANK[role] >= RANK[min];
}

/** PII in the browser: phone numbers are for roles >= rep. Ops runs the system, not the sales floor. */
export function canSeePhone(role: Role): boolean {
  return atLeast(role, 'rep');
}

/** The Explain panel exposes model cost and prompt internals — ops and management, not the floor. */
export function canSeeExplain(role: Role): boolean {
  return role === 'ops' || role === 'manager' || role === 'owner';
}

/** Steering the agent changes what every future customer is told. */
export function canSteer(role: Role): boolean {
  return role === 'ops' || role === 'manager' || role === 'owner';
}

export function canManagePeople(role: Role): boolean {
  return role === 'owner' || role === 'manager';
}

/**
 * The write gates, mirrored from the API's own `minRole` declarations.
 *
 * These do not ENFORCE anything — the server does, and `ops` calling any of them gets a 403 with
 * or without this file. They exist so a role that may not do a thing is shown a disabled control
 * with the reason on it, rather than a live button that fails after the click. A button that
 * looks armed and is not is how somebody concludes the console is broken.
 */
/** `POST /leads/:id/messages`, `/stage`, and every `/handoffs/:id/*` are `minRole: rep`. */
export function canWrite(role: Role): boolean {
  return atLeast(role, 'rep');
}

/** `POST /catalogue/media/:id` is `minRole: manager`. */
export function canEditMedia(role: Role): boolean {
  return atLeast(role, 'manager');
}

/** `POST /people` and `/people/:id/deactivate` are owner-only; `GET /people` is manager and up. */
export function canChangePeople(role: Role): boolean {
  return role === 'owner';
}

/**
 * Every `POST /campaigns*` route — create, edit, dry-run, start, pause, cancel — is
 * `minRole: manager`. A campaign is the one action in this product that reaches thousands of
 * customers at once and cannot be recalled, so the floor does not get a live Start button.
 */
export function canManageCampaigns(role: Role): boolean {
  return atLeast(role, 'manager');
}

/** `POST /quality/thumbs` and `POST /quality/corrections` are `minRole: rep`. */
export function canRateReplies(role: Role): boolean {
  return atLeast(role, 'rep');
}

/**
 * `POST /quality/corrections/:id/close` is `anyRole: [owner, manager]`.
 *
 * Deliberately NOT the same gate as `canRateReplies`: a rep says the reply was wrong and writes
 * what it should have said; deciding what that MEANS — a rule changes, a golden case is added, or
 * the agent was right enough — is a manager's call. The screen has to carry both gates because
 * one page holds both actions.
 */
export function canCloseCorrection(role: Role): boolean {
  return role === 'owner' || role === 'manager';
}

/**
 * `POST /onboarding/steps/:step` and `POST /onboarding/checklist` are `anyRole: [owner, ops]`.
 *
 * This is the capability that proves the rank alone cannot answer the question. The ladder is not
 * monotone here: `ops` sits at the BOTTOM of the rank because an operator must not inherit the
 * client's customer phone numbers, and `ops` is nevertheless exactly who brings a tenant up.
 * `manager` and `rep` sit above `ops` and may not run a step at all. A set, not a threshold.
 */
export function canOnboard(role: Role): boolean {
  return role === 'owner' || role === 'ops';
}

/**
 * `POST /onboarding/status` is `minRole: owner`. Moving a tenant to live is the moment the agent
 * starts answering real customers, and it is the owner's signature on that, nobody else's — not
 * even the operator who ran every step and satisfied every gate.
 */
export function canMoveTenantStatus(role: Role): boolean {
  return role === 'owner';
}

/**
 * The sentence a disabled control carries, in the house shape: what the action needs, then what
 * you actually are.
 *
 * Both halves earn their place. "You need to be a manager" without the role leaves somebody
 * wondering whether they already are one; the role without the requirement tells them nothing
 * about who to ask. Named `needs` rather than built from the role constant because the useful
 * phrasing differs per screen — "for a manager or an owner" reads better on a set than "manager
 * or above" does, and a set is what several of these gates are.
 *
 * Returns `undefined` when the role may act, so it drops straight into `title={gate}` and
 * `disabled={Boolean(gate)}` without a ternary at every call site.
 */
export function roleGate(may: boolean, needs: string, role: Role): string | undefined {
  return may ? undefined : `${needs} — you are signed in as ${role}.`;
}

/**
 * What to render where a phone number would go.
 * Returns the number for roles that may see it, and the stable contact hash for those that may
 * not — so rows stay distinguishable without leaking the number.
 */
export function phoneFor(
  role: Role,
  contact: { phone: string | null; contactHash: string },
): { text: string; redacted: boolean } {
  if (!canSeePhone(role) || contact.phone === null) {
    return { text: contact.contactHash, redacted: true };
  }
  return { text: contact.phone, redacted: false };
}
